import React, { useState, useEffect, useContext } from 'react'
import { useCookies } from 'react-cookie'
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import { createMuiTheme, ThemeProvider } from '@material-ui/core'
import { blue } from '@material-ui/core/colors'

import './App.css'
import {
  getWorkHistory,
  getReferences,
  getMetaData
} from './api/proReference.service'
import ProReference from './pages/proReference/proReference'
import { UserContext } from './context/context'
import queryString from 'query-string'

const textBoxColor = '#F7F7F7'

const commonTheme = {
  typography: {
    fontFamily: 'Open Sans',
    h6: {
      color: '#333333',
      fontWeight: '500',
      fontSize: '16px'
    },
    h5: {
      color: '#333333',
      fontWeight: '450',
      fontSize: '14px'
    },
    h4: {
      color: '#333333',
      fontSize: '24px'
    },
    body1: {
      color: '#555555',
      fontSize: '14px'
    },
    body2: {
      color: '#555555',
      fontSize: '16px'
    },
    subtitle2: {
      color: '#a6a6a6'
    },
    subtitle1: {
      color: '#555555',
      fontSize: '12px'
    }
  },
  overrides: {
    MuiFormHelperText: {
      root: {
        lineHeight: '1.2'
      },
      contained: {
        fontSize: '10px'
      }
    },
    MuiInputBase: {
      input: {
        background: textBoxColor,
        '&:hover': {
          background: textBoxColor
        }
      }
    },
    MuiSelect: {
      select: {
        '&:focus': {
          background: textBoxColor,
          backgroundColor: textBoxColor
        }
      }
    },
    MuiFilledInput: {
      root: {
        '&.Mui-focused': {
          background: textBoxColor
        },
        background: textBoxColor,
        '&:hover': {
          background: textBoxColor,
          backgroundColor: textBoxColor,
          '@media (hover: none)': {
            backgroundColor: textBoxColor
          }
        },
        focused: {
          background: `${textBoxColor} !important`
        }
      },
      multiline: {
        background: textBoxColor,
        '&:hover': {
          background: textBoxColor
        }
      },
      adornedEnd: {
        background: textBoxColor
      }
    },
    MuiCheckbox: {
      colorSecondary: {
        color: 'grey',
        '&$checked': {
          color: '#0098D8'
        }
      }
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: '#FFF'
      }
    }
  }
}

export const mainTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#0098DB'
    },
    secondary: {
      main: '#003C69'
    },
    error: {
      main: '#D9553D'
    }
  },
  typography: {
    fontFamily: 'Open Sans',
    body1: {
      color: '#555555',
      fontSize: '14px'
    }
  }
}, commonTheme)

export const radioButtonTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#0098DB'
    },
    secondary: {
      main: '#003C69'
    },
    error: {
      main: '#D9553D'
    }
  },
  typography: {
    fontFamily: 'Open Sans',
    body1: {
      color: '#003C69'
    }
  }
})

export const textFieldTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#6639B7'
    },
    secondary: {
      main: '#003C69'
    },
    error: {
      main: '#D9553D'
    }
  },
  typography: {
    fontFamily: 'Open Sans',
    body1: {
      fontSize: '14px'
    }
  }
}, commonTheme)

export const textAreaTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#6639B7'
    },
    secondary: {
      main: '#003C69'
    },
    error: {
      main: '#D9553D'
    }
  },
  typography: {
    fontFamily: 'Open Sans',
    body1: {
      fontSize: '14px'
    }
  },
  overrides: {
    MuiFormHelperText: {
      root: {
        color: '#333333',
        lineHeight: '1.3'
      },
      contained: {
        marginLeft: '0px',
        marginRight: '0px',
        fontSize: '10px'
      }
    }
  }
}, commonTheme)

function App (props) {
  const [workHistory, setWorkHistory] = useState([]) // Contains list of work histories
  const [references, setReferences] = useState([])
  const [metaData, setMetaData] = useState({})
  const [dataLoading, setDataLoading] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [errorLoadingContent, setErrorLoadingContent] = useState(false)
  const [noworkHistory, setNoWorkHistory] = useState(false)
  const { user } = useContext(UserContext)
  let workHistoryID = queryString.parse(props.location.search).whid ?? -1
  let referenceId = queryString.parse(props.location.search).rid

  useEffect(() => {
    if (!dataLoaded && user.tid !== null) {
      // GET WORK HISTORY
      // FIXME: Replace the TID and WorkHistoryID with ones from either the cookie or the URL params
      if (!dataLoading && !dataLoaded) {
        setDataLoading(true)
        getWorkHistory(user.tid, workHistoryID, user)
          .then((whResponse) => {
            setWorkHistory({
              ...whResponse.data,
              workHistory: {
                ...whResponse.data.workHistory[0]
              }
            })

            if (whResponse.status !== 200) {
              // TODO: Handle error
            }

            getMetaData(user).then((metadataResponse) => {
              if (metadataResponse.status === 200) {
                metadataResponse.data.clientPhoneTypes = [
                  {
                    value: 6,
                    label: 'Mobile'
                  },
                  {
                    value: 5,
                    label: 'Home'
                  },
                  {
                    value: 1,
                    label: 'Work'
                  }
                ]
                metadataResponse.data.jobTitles = metadataResponse.data.jobTitles.map(
                  (item) => {
                    return {
                      value: item.id,
                      label: item.description
                    }
                  }
                )
                metadataResponse.data.referenceEvaluationRatingTypes = metadataResponse.data.referenceEvaluationRatingTypes
                  .sort((a, b) => {
                    return a.id < b.id ? 1 : -1
                  })
                  .map((item) => {
                    return {
                      value: parseInt(item.id),
                      label:
                        parseInt(item.id) === 4
                          ? '1. No Experience'
                          : parseInt(item.id) === 3
                            ? '2. Intermittent'
                            : parseInt(item.id) === 2
                              ? '3. Proficient'
                              : '4. Expert'
                    }
                  })
                setMetaData(metadataResponse.data)

                // GET REFERENCES
                if (props.isEvaluator) {
                  getReferences(
                    user.tid,
                    workHistoryID,
                    user,
                    referenceId
                  ).then((referenceResponse) => {
                    // TODO: Handle when no references are returned. E.g., for first entering references for a work history
                    if (
                      referenceResponse.data.workHistory[0].references ===
                        null ||
                      referenceResponse.data.workHistory[0].references ===
                        undefined ||
                      referenceResponse.data.workHistory[0].references
                        .length === 0
                    )
                      setReferences([{}])
                    else {
                      const references = referenceResponse.data.workHistory[0].references.map(
                        (item) => {
                          return {
                            ...item,
                            evaluations: item.evaluations ?? [],
                            ageCompetencies: item.ageCompetencies ?? []
                          }
                        }
                      )
                      setReferences(references)
                    }

                    if (referenceResponse.status !== 200) {
                      // TODO: Handle error
                    }
                  }).catch(() => {
                    setErrorLoadingContent(true)
                  })
                } else {
                  if (whResponse.data.workHistory.length > 0) {
                    setNoWorkHistory(false)
                    setReferences([
                      {
                        positionHeld:
                          whResponse.data.workHistory[0].positionHeld,
                        unitName: whResponse.data.workHistory[0].unit,
                        supervisorPhoneType: 6,
                        amhTitleId: metadataResponse.data.jobTitles[0].value,
                        amhTitle: metadataResponse.data.jobTitles[0].label,
                        title: metadataResponse.data.jobTitles[0].label
                      }
                    ])
                  } else {
                    setNoWorkHistory(true)
                    setReferences([
                      {
                        positionHeld: '',
                        unitName: '',
                        unitDescription: '',
                        supervisorPhoneType: 6
                      }
                    ])
                  }
                }
              }
            }).catch(() => {
              setErrorLoadingContent(true)
            })
          })
          .catch((error) => {
            if (error.message.includes('401'))
              alert('You are not authorized to view this page')
            setErrorLoadingContent(true)
          })
      }

      if (
        workHistory.length !== 0 &&
        Object.keys(metaData).length !== 0 &&
        references.length === 1
      )
        setDataLoaded(true)
    }
  }, [
    workHistory,
    references,
    metaData,
    dataLoaded,
    dataLoading,
    props.isEvaluator,
    workHistoryID,
    user,
    referenceId,
    noworkHistory
  ])

  return (
    <ProReference
      {...props}
      workHistory={workHistory}
      noworkHistory={noworkHistory}
      references={references}
      isEvaluator={props.isEvaluator}
      dataLoaded={dataLoaded}
      errorLoadingContent={errorLoadingContent}
      jobTitles={metaData.jobTitles}
      phoneTypes={metaData.clientPhoneTypes}
      referenceEvaluationRatingTypes={metaData.referenceEvaluationRatingTypes}
      referenceEvaluationCategoryTypes={
        metaData.referenceEvaluationCategoryTypes
      }
      referenceAgeTypes={metaData.referenceAgeTypes}
      loadContentAgain={() => {
        setDataLoaded(false)
        setDataLoading(false)
        setErrorLoadingContent(false)
      }}
    />
  )
}

export default App
